'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';

import type { LandscapeVisionDisplay } from '@/types';
import { getMediaData, getFilmData } from '@/lib/utils/media.utils';

import dynamic from 'next/dynamic';
import VideoProgress from './ui/progress';
import VolumeControl from './ui/volume';

const ImmersiveScene = dynamic(() => import('./Immersive/ImmersiveScene'), { ssr: false });

interface LandscapeVisionDisplayProps {
  layout: LandscapeVisionDisplay;
}
export const Landscape_Vision_Display: React.FC<LandscapeVisionDisplayProps> = ({ layout }) => {
  const [showImmersive, setShowImmersive] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const bgImage = getMediaData(layout.image);
  const videoData = getFilmData(layout.video);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (showImmersive) {
      document.body.classList.add('full-screen');
    } else {
      document.body.classList.remove('full-screen');
    }

    return () => {
      document.body.classList.remove('full-screen');
    };
  }, [showImmersive]);

  const handlePlayToggle = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const handleProgressChange = useCallback((progress: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = progress;
      setCurrentTime(progress);
    }
  }, []);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, []);

  return (
    <section className="w-full relative">
      <div
        className={`p-[15px] rounded-md mb-[12px] flex
           flex-col justify-between relative`}
        style={{
          height: '700px',
          background: (bgImage.url ? `url('${bgImage.url}')` : ' ') + (layout.bgColor ? layout.bgColor : 'white'),
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }}
      >
        {/* <div className="absolute top-4 right-4 z-12 flex items-center gap-4">
          <button
            onClick={() => {}}
            className="text-[10px]  px-2 py-1 
            rounded-md cursor-pointer transition-colors border border-white text-white"
          >
            RENDER
          </button>
          <button
            onClick={() => {
              setShowImmersive(true);
            }}
            className="text-[10px]  px-2 py-1 
            rounded-md cursor-pointer transition-colors border border-white text-white opacity-40 hover:opacity-100"
          >
            IMMERSIVE
          </button>
        </div> */}
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-12 flex items-center gap-4">
          <button
            onClick={() => {
              setShowImmersive(true);
            }}
            className="text-[11px] px-2 py-1
            rounded-sm cursor-pointer transition-colors border bg-white text-black hover:bg-white/70"
          >
            CLICK TO ENTER IMMERSIVE MODE
          </button>
        </div>
      </div>

      {videoData.url && (
        <VideoProgress
          className="bottom-4 center-4 z-[100] left-[50%] -translate-x-1/2"
          initialProgress={currentTime}
          duration={videoDuration}
          onChange={handleProgressChange}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onPlayToggle={handlePlayToggle}
        />
      )}

      {videoData.url && (
        <VolumeControl
          onChange={volume => {
            if (videoRef.current) {
              videoRef.current.volume = volume / 100;
              videoRef.current.muted = volume === 0;
            }
          }}
        />
      )}

      {videoData.url && (
        <video
          ref={videoRef}
          src={videoData.url}
          autoPlay
          muted
          loop
          onLoadedMetadata={() => {
            if (videoRef.current) {
              setVideoDuration(videoRef.current.duration);
            }
          }}
          onTimeUpdate={() => {
            if (videoRef.current && !isDragging) {
              setCurrentTime(videoRef.current.currentTime);
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
          }}
        />
      )}
      {showImmersive && (
        <div className="fixed inset-0 z-100">
          <ImmersiveScene onClose={() => setShowImmersive(false)} />
        </div>
      )}
    </section>
  );
};
